(() => {
  'use strict';

  const script = document.currentScript;
  const navLabel = (script && script.dataset.policiesNavLabel) || '';

  const MINIMUM_SECTIONS = 4;

  const container = document.querySelector('.shopify-policy__container');
  if (!container || container.hasAttribute('data-policies-enhanced')) return;

  const policyBody = container.querySelector('.shopify-policy__body');
  const rte = policyBody && policyBody.querySelector('.rte');
  if (!rte) return;

  /**
   * Shopify's `handleize`, narrowed to what a heading can hold.
   *
   * @param {string} value - Heading text.
   * @returns {string} Handle safe to use as an id fragment.
   */
  function handleize(value) {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/['\u2018\u2019]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * @param {string} base - Preferred id.
   * @param {number} index - Position of the heading, for the last-resort name.
   * @returns {string} An id no other element in the document is using.
   */
  function uniqueId(base, index) {
    let candidate = base || `policy-section-${index + 1}`;
    let suffix = 2;

    while (document.getElementById(candidate)) {
      candidate = `${base || 'policy-section'}-${suffix}`;
      suffix += 1;
    }

    return candidate;
  }

  /**
   * A paragraph that is nothing but a link to elsewhere on this page — the shape a hand-authored table of contents takes in policy HTML.
   *
   * @param {Element} element - Candidate node.
   * @returns {boolean} Whether the paragraph is a standalone in-page anchor.
   */
  function isAnchorParagraph(element) {
    if (element.tagName !== 'P' || element.children.length !== 1) return false;

    const link = element.firstElementChild;
    if (link.tagName !== 'A') return false;
    if (!(link.getAttribute('href') || '').startsWith('#')) return false;

    return element.textContent.trim() === link.textContent.trim();
  }

  /**
   * Wraps a heading's leading "1." in a marker span so wrapped lines align with the words rather than a guessed indent.
   *
   * @param {Element} heading - Numbered section heading.
   */
  function markNumber(heading) {
    const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
    const node = walker.nextNode();
    if (!node) return;

    const match = node.nodeValue.match(/^(\s*\d+\s*[.)]\s+)/);
    if (!match) return;

    const marker = document.createElement('span');
    marker.className = 'policies__section-title-marker';
    marker.textContent = match[1];
    node.nodeValue = node.nodeValue.slice(match[1].length);
    node.parentNode.insertBefore(marker, node);
  }

  const children = [...rte.children];
  const levels = children
    .filter((element) => /^H[1-6]$/.test(element.tagName))
    .map((element) => Number(element.tagName.slice(1)));

  if (levels.length < MINIMUM_SECTIONS) return;

  const headingTag = `H${Math.min(...levels)}`;
  const headings = children.filter((element) => element.tagName === headingTag);
  if (headings.length < MINIMUM_SECTIONS) return;

  const isNumbered = (text) => /^\s*\d+\s*[.)]\s/.test(text);

  headings.forEach((heading, index) => {
    if (!heading.id) heading.id = uniqueId(handleize(heading.textContent), index);
    heading.classList.add('policies__section-title');
    if (isNumbered(heading.textContent)) {
      heading.classList.add('policies__section-title--numbered');
      markNumber(heading);
    }
  });

  const headingIds = new Set(headings.map((heading) => heading.id));
  const firstHeadingIndex = children.indexOf(headings[0]);

  const introNodes = children.slice(0, firstHeadingIndex).filter((element) => {
    if (!isAnchorParagraph(element)) return true;
    return !headingIds.has(element.firstElementChild.getAttribute('href').slice(1));
  });

  const intro = document.createElement('div');
  intro.className = 'policies__intro';
  intro.append(...introNodes);

  const sections = document.createElement('div');
  sections.className = 'policies__sections';

  headings.forEach((heading, index) => {
    const start = children.indexOf(heading);
    const end = index + 1 < headings.length ? children.indexOf(headings[index + 1]) : children.length;

    const section = document.createElement('section');
    section.className = 'policies__section';
    section.setAttribute('aria-labelledby', heading.id);
    section.dataset.policiesSection = heading.id;

    const layout = document.createElement('div');
    layout.className = 'policies__section-layout';

    const content = document.createElement('div');
    content.className = 'policies__section-body';
    content.append(...children.slice(start + 1, end));

    layout.append(heading, content);
    section.append(layout);
    sections.append(section);
  });

  const nav = document.createElement('nav');
  nav.className = 'policies__nav dieux-tabs--column';
  if (navLabel) nav.setAttribute('aria-label', navLabel);

  const list = document.createElement('ul');
  list.className = 'policies__nav-list dieux-tabs__tablist';
  list.setAttribute('role', 'list');

  const links = headings.map((heading) => {
    const item = document.createElement('li');
    item.className = 'policies__nav-item';

    const link = document.createElement('a');
    link.className = 'policies__nav-link dieux-tabs__tab';
    if (isNumbered(heading.textContent)) link.classList.add('policies__nav-link--numbered');
    link.href = `#${heading.id}`;
    link.dataset.policiesTarget = heading.id;

    const stack = document.createElement('span');
    stack.className = 'dieux-tabs__label-stack';

    const measure = document.createElement('span');
    measure.className = 'dieux-tabs__label-measure';
    measure.setAttribute('aria-hidden', 'true');
    measure.textContent = heading.textContent;

    const label = document.createElement('span');
    label.className = 'dieux-tabs__label';
    label.textContent = heading.textContent;

    stack.append(measure, label);
    link.append(stack);
    item.append(link);
    list.append(item);

    return link;
  });

  nav.append(list);

  const sidebar = document.createElement('div');
  sidebar.className = 'policies__sidebar';

  rte.replaceChildren(intro, sections);
  container.insertBefore(sidebar, policyBody);

  const title = container.querySelector('.shopify-policy__title');
  if (title) sidebar.append(title);
  sidebar.append(nav);

  container.setAttribute('data-policies-enhanced', '');

  const introRte = document.createElement('div');
  introRte.className = rte.className;

  const introBody = document.createElement('div');
  introBody.className = policyBody.className;
  introBody.append(introRte);

  /** @returns {boolean} Whether the title and navigation share the sticky sidebar column at this width. */
  function hasSidebarLayout() {
    return getComputedStyle(nav).getPropertyValue('--policies-nav-tracking').trim() === '1';
  }

  /**
   * Keeps the intro between the title and the navigation in the DOM wherever the layout stacks them.
   *
   * @param {boolean} sidebarLayout - Whether the title and navigation share the sticky sidebar column.
   */
  function placeIntro(sidebarLayout) {
    const host = sidebarLayout ? rte : introRte;
    if (intro.parentNode === host) return;

    const focused = intro.contains(document.activeElement) ? document.activeElement : null;

    if (sidebarLayout) {
      rte.prepend(intro);
      introBody.remove();
    } else {
      introRte.append(intro);
      sidebar.insertBefore(introBody, nav);
    }

    if (focused) focused.focus({ preventScroll: true });
  }

  placeIntro(hasSidebarLayout());

  const sectionElements = [...sections.children];
  /* Resolved from CSS in `syncTracking` before the first paint. */
  let trackingEnabled = false;
  let revealKey = null;
  let spy = null;

  /**
   * Keeps the current link inside the navigation's own scrollport.
   *
   * @param {HTMLAnchorElement} link - Link that just became current.
   */
  function revealLink(link) {
    if (!window.DieuxFrame) return;

    window.DieuxFrame.measure((revealKey ??= Symbol('policies-reveal')), () => {
      if (!list.isConnected || list.scrollHeight - list.clientHeight < 1) return;

      const styles = getComputedStyle(list);
      const insetStart = Number.parseFloat(styles.scrollPaddingBlockStart) || 0;
      const insetEnd = Number.parseFloat(styles.scrollPaddingBlockEnd) || 0;
      const listBounds = list.getBoundingClientRect();
      const linkBounds = link.getBoundingClientRect();

      const above = linkBounds.top - insetStart - listBounds.top;
      const below = linkBounds.bottom + insetEnd - listBounds.bottom;

      let delta = 0;
      if (above < 0) delta = above;
      else if (below > 0) delta = below;
      if (delta === 0) return;

      const top = list.scrollTop + delta;
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth';

      window.DieuxFrame.write(() => {
        if (list.isConnected) list.scrollTo({ top, behavior });
      });
    });
  }

  function linkFor(id) {
    return links.find((link) => link.dataset.policiesTarget === id) || null;
  }

  /**
   * Writes the tracked section onto the links, or clears every link where this
   * width has no current section to show.
   *
   * @returns {HTMLAnchorElement|null} The link now carrying the state, if any.
   */
  function paintCurrent() {
    const currentLink = trackingEnabled ? linkFor(spy?.currentId) : null;

    links.forEach((link) => {
      if (link === currentLink) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });

    return currentLink;
  }

  /** Whether the navigation reports a reading position at this width. */
  function syncTracking() {
    const next = hasSidebarLayout();
    placeIntro(next);
    if (next === trackingEnabled) return;

    trackingEnabled = next;
    paintCurrent();
  }

  function start() {
    spy = window.DieuxScrollSpy?.create({
      name: 'policies',
      targets: sectionElements,
      idOf: (section) => section.dataset.policiesSection,
      onCurrent: (id, changed) => {
        if (!changed) return;

        const currentLink = paintCurrent();
        if (currentLink) revealLink(currentLink);
      },
      tail: { host: container, property: '--policies-tail-room' },
      onSync: syncTracking,
    });
    if (!spy) return;

    syncTracking();

    links.forEach((link) => {
      link.addEventListener('click', () => spy.request(link.dataset.policiesTarget));
    });

    spy.start(spy.idFromHash() || headings[0].id);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
